import { Link } from "react-router-dom";

import { Icon, type IconName } from "../../components/icons";
import { PRECIO_TEXTO, PRIMER_TORNEO_TEXTO, WHATSAPP_AUTO_LIVE } from "./content";
import { CtaBand } from "./HomePage";
import { usePageMeta } from "./hooks";
import { PlayerHomeMock } from "./mockups/PlayerHomeMock";
import { TournamentCards } from "./TournamentCards";
import { HOME_GRID_SIZE, useTournamentDirectory } from "./useTournamentDirectory";
import "./PlayerPage.css";

interface PlayerBenefit {
  icon: IconName;
  title: string;
  copy: string;
}

const PLAYER_BENEFITS: PlayerBenefit[] = [
  {
    icon: "trophy",
    title: "Torneos públicos",
    copy: "Encuentra torneos con inscripciones abiertas e inscríbete desde el celular.",
  },
  {
    icon: "calendar",
    title: "Todo tu calendario",
    copy: "Partidos, rivales, horarios, cuadro, resultados y ranking en la app que instalas desde el navegador.",
  },
  {
    icon: "bell",
    title: "Avisos de tus partidos",
    copy: WHATSAPP_AUTO_LIVE
      ? "Recordatorio por WhatsApp y en la app un día antes y una hora antes de cada partido, y aviso si cambia tu horario."
      : "Recordatorio en la app un día antes y una hora antes de cada partido, y aviso si cambia tu horario.",
  },
  {
    icon: "users",
    title: "Busca amigos y retas",
    copy: "Agrega amigos, arma partidos amistosos y captura el marcador.",
  },
  {
    icon: "chart",
    title: "Lleva tu récord",
    copy: "Historial de partidos, victorias, derrotas y estadísticas de todos tus torneos.",
  },
  {
    icon: "ticket",
    title: "Credencial digital",
    copy: "Tu credencial de torneo con QR verificable, siempre en tu celular.",
  },
];

/** /jugador: lo que un jugador hace en Torneos123 (sin reservas de cancha ni
 * credencial de socio: ese módulo está congelado). */
export function PlayerPage() {
  usePageMeta("player");
  const { tournaments, status } = useTournamentDirectory();
  return (
    <main className="t123-player">
      <section className="t123-subhero t123-player__hero" aria-labelledby="t123-player-title">
        <div className="t123-shell t123-subhero__grid">
          <div className="t123-player__copy">
            <span className="t123-kicker">Para jugadores</span>
            <h1 id="t123-player-title">Encuentra torneos, juega más y mide tu progreso.</h1>
            <p>
              Un perfil para descubrir torneos públicos, revisar horarios, consultar resultados y competir con jugadores de
              tu nivel.
            </p>
            <div className="t123-hero__actions">
              <Link className="t123-btn t123-btn--primary t123-btn--arrow" to="/torneos">
                Ver torneos
              </Link>
              <Link className="t123-btn t123-btn--outline t123-btn--arrow" to="/login">
                Iniciar sesión
              </Link>
            </div>
            <p className="t123-player__note">
              ¿Todavía no tienes cuenta? Inscríbete a un torneo: cuando el organizador aprueba tu inscripción, te llegan por
              correo tu usuario y contraseña.
            </p>
          </div>
          <div className="t123-player__mock">
            <PlayerHomeMock />
          </div>
        </div>
      </section>

      <section className="t123-benefits t123-player__benefits" aria-labelledby="t123-player-benefits-title">
        <div className="t123-shell">
          <div className="t123-section-heading t123-section-heading--center">
            <h2 id="t123-player-benefits-title">Todo lo que puedes hacer como jugador</h2>
            <p>Torneos, avisos de tus partidos, retas con amigos y tu récord, en una sola app.</p>
          </div>
          <div className="t123-benefit-grid">
            {PLAYER_BENEFITS.map((item) => (
              <article key={item.title}>
                <span aria-hidden="true">
                  <Icon name={item.icon} />
                </span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="t123-section t123-section--soft t123-player__upcoming"
        aria-labelledby="t123-player-upcoming-title"
      >
        <div className="t123-shell">
          <div className="t123-player__head">
            <h2 id="t123-player-upcoming-title">Inscríbete a tu próximo torneo</h2>
            <Link className="t123-player__see-all" to="/torneos">
              Ver todos <span aria-hidden="true">›</span>
            </Link>
          </div>
          <TournamentCards tournaments={tournaments} status={status} limit={HOME_GRID_SIZE} withPromo />
        </div>
      </section>

      <CtaBand
        title="¿Organizas torneos?"
        copy={`${PRIMER_TORNEO_TEXTO}. Después, ${PRECIO_TEXTO}.`}
        primary={{ to: "/organizador#demo", label: "Pedir demo" }}
        secondary={{ to: "/organizador", label: "Para organizadores" }}
      />
    </main>
  );
}
